import React, { useRef } from 'react';
import { Search, X, Clock, Check, LayoutGrid, Rows3, Plus, ListFilter } from 'lucide-react';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import Tooltip from '../ui/Tooltip';
import BoardsScopeChips from './BoardsScopeChips';
import { SORT_OPTIONS } from '../../extensions/bundled/task-manager/boardsOverview';
import { VIEW_CARDS, VIEW_ROWS } from '../../extensions/bundled/task-manager/boardsLocalPrefs';
import './BoardFilterBar.css';
import useToolbarCompact from './useToolbarCompact';
import './BoardsOverview.css';
import LocalCapHint from '../plans/LocalCapHint';

/**
 * The Boards overview toolbar (card 7.2): "Filter boards" search, the scope
 * segments with counts, the "Has overdue" / "Hide completed" chips, Sort, the
 * card/row toggle and an accent "New board" button (the dashed tile in the
 * grid stays too).
 *
 * The search is deliberately the SAME control as the in-board filter bar's
 * search (task-board-filter-search): the two do the same job one level apart.
 * Its ✕ slot is always there so the box keeps one size typed or empty (set
 * 9c), and the ✕ never takes focus from the input.
 */
// Compact toolbar (owner, 14.10.2026): the label hides and the Tooltip keeps
// the words; aria-label names the chip either way.
function QuickChip({ on, icon, label, count, onClick }) {
  return (
    <Tooltip content={label} side="bottom">
      <button
        type="button"
        className={`boards-quick-chip${on ? ' boards-quick-chip--on' : ''}`}
        aria-pressed={on}
        aria-label={`${label} ${count}`}
        onClick={onClick}
      >
        {icon}
        <span className="boards-quick-chip-label">{label} </span>
        <span className="boards-quick-chip-count">{count}</span>
      </button>
    </Tooltip>
  );
}

function ViewToggle({ view, onView }) {
  const btn = (value, label, Icon) => (
    <Tooltip content={label} side="bottom">
      <button
        type="button"
        aria-label={label}
        aria-pressed={view === value}
        className={`boards-seg-btn boards-seg-btn--icon${view === value ? ' boards-seg-btn--on' : ''}`}
        onClick={() => onView(value)}
      >
        <Icon size={13} aria-hidden />
      </button>
    </Tooltip>
  );
  return (
    <div className="boards-seg" role="group" aria-label="Boards view">
      {btn(VIEW_CARDS, 'Card view', LayoutGrid)}
      {btn(VIEW_ROWS, 'Row view', Rows3)}
    </div>
  );
}

export default function BoardsOverviewToolbar({
  boardFilter,
  setBoardFilter,
  boardFilterRef,
  chips,
  scope,
  onScope,
  quick,
  quickCounts,
  onQuick,
  sort,
  onSort,
  view,
  onView,
  onNewBoard,
}) {
  // One row (owner, 14.10.2026): too narrow → the chips and "New board" show
  // only their icon, like the open board's toolbar.
  const ref = useRef(null);
  const compact = useToolbarCompact(ref);
  return (
    <div className={`boards-toolbar${compact ? ' boards-toolbar--compact' : ''}`} ref={ref}>
      <div className="boards-toolbar-left task-board-home-search" data-compact-measure>
        <div className="task-board-filter-search">
          <Search size={13} aria-hidden />
          <input
            ref={boardFilterRef}
            type="text"
            value={boardFilter}
            placeholder="Filter boards…"
            aria-label="Filter boards"
            autoComplete="off"
            onChange={(e) => setBoardFilter(e.target.value)}
          />
          {boardFilter ? (
            <button type="button" aria-label="Clear filter" onMouseDown={(e) => e.preventDefault()} onClick={() => setBoardFilter('')}>
              <X size={12} />
            </button>
          ) : (
            <span className="task-board-filter-clear-slot" aria-hidden />
          )}
        </div>
        <BoardsScopeChips chips={chips} selected={scope} onSelect={onScope} />
        <QuickChip
          on={quick.overdue}
          icon={<Clock size={12} aria-hidden />}
          label="Has overdue"
          count={quickCounts.overdue}
          onClick={() => onQuick({ overdue: !quick.overdue })}
        />
        <QuickChip
          on={quick.hideCompleted}
          icon={<Check size={12} aria-hidden />}
          label="Hide completed"
          count={quickCounts.completed}
          onClick={() => onQuick({ hideCompleted: !quick.hideCompleted })}
        />
      </div>
      <div className="boards-toolbar-right">
        <div className="boards-sort">
          <ListFilter size={12} className="boards-sort-icon" aria-hidden />
          <CompactSelectPicker
            value={sort}
            onChange={onSort}
            options={SORT_OPTIONS}
            className="boards-sort-picker"
            menuMinWidth={170}
            aria-label="Sort boards"
          />
        </div>
        <ViewToggle view={view} onView={onView} />
        {/* Plans v2 card 3.3: "3 / 1" once the plan's cap is reached; the
            button below still opens the upgrade, never a dead button. */}
        <LocalCapHint kind="boards" />
        <LocalCapHint kind="tasks" />
        <Tooltip content="New board" side="bottom">
          <button type="button" className="boards-new-btn" aria-label="New board" onClick={onNewBoard}>
            <Plus size={13} aria-hidden />
            <span className="boards-new-btn-label">New board</span>
          </button>
        </Tooltip>
      </div>
    </div>
  );
}
